import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Zap, ShieldCheck, AlertTriangle } from "lucide-react";

/*  Safe bulk merge — preview & confirm.
 *  The server only lists groups where every profile shares the same name AND
 *  the same real email, and every non-primary profile is completely empty:
 *  no appointments, orders, series, gift cards, loyalty points, medical
 *  answers or guest preferences. Groups are recomputed on apply, so a stale
 *  preview can never merge a profile that gained history in the meantime.
 */
export default function SafeMergeDialog({ open, onOpenChange, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const r = await staffApi.get("/customers/duplicates/safe-merge/preview");
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await staffApi.post("/customers/duplicates/safe-merge/apply", { primary_ids: [] });
      if (r.data.failures?.length) {
        toast.error(`${r.data.merged_profiles} merged · ${r.data.failures.length} group(s) failed`);
      } else {
        toast.success(`${r.data.merged_profiles} duplicate profiles merged into ${r.data.merged_groups} guests`);
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const groups = data?.groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="safe-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Bulk merge safe duplicates</DialogTitle>
          <DialogDescription>
            Only groups where the name and email match exactly and every extra profile is completely empty — no appointments, orders, packages, gift cards, points, medical answers or preferences.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="safe-merge-loading">Checking every duplicate group…</p>}

        {!loading && groups.length === 0 && (
          <div className="py-8 text-center" data-testid="safe-merge-empty">
            <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <p className="text-sm font-medium">No safe groups right now.</p>
            <p className="text-xs text-muted-foreground mt-1">Remaining pairs carry history on both sides — review those one by one.</p>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="safe-merge-summary">
              <strong>{data.profile_count}</strong> empty duplicate profile{data.profile_count === 1 ? "" : "s"} will be merged into <strong>{data.group_count}</strong> guest record{data.group_count === 1 ? "" : "s"}. History, points and preferences are combined — nothing is deleted.
            </div>
            <div className="max-h-[320px] overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="safe-merge-list">
              {groups.map((g) => (
                <div key={g.primary_id} className="p-2.5 text-xs" data-testid={`safe-merge-group-${g.primary_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{g.primary.full_name}</span>
                    <span className="text-muted-foreground shrink-0">{g.primary.appointments_count} appts · {g.primary.orders_count} orders</span>
                  </div>
                  <div className="text-muted-foreground break-all">{g.email}</div>
                  <div className="text-amber-700 mt-0.5">
                    absorbs {g.merge_ids.length} empty duplicate{g.merge_ids.length === 1 ? "" : "s"}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-px" />
              Duplicate profiles are archived (not deleted), their logins deactivated and their emails freed. Every merge is written to the security audit log.
            </p>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="safe-merge-cancel">Cancel</Button>
          <Button onClick={apply} disabled={busy || loading || groups.length === 0} className="gap-1.5" data-testid="safe-merge-confirm">
            <Zap className="w-4 h-4" /> {busy ? "Merging…" : `Merge ${data?.profile_count || 0} profiles`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
